import { Box } from '@chakra-ui/react';
import { Tooltip } from '@/components/v4';
import OrderStatuses, {
    ACTIVE_ORDER_LABEL,
    isActiveOrder,
} from './orderStatuses';

// Palette mirrors the Numbers table status pill
// (pages/telephony/numbers/edit/cellRenderer/StatusCellRenderer.module.css).
// Every tag background is translucent and no text colour is set, so the pill
// inherits the grid's foreground colour and stays legible in light and dark mode.
const ORDER_STATUS_PILL_MAP = {
    Completed: {
        tag: { backgroundColor: '#84ca8130', border: '1.5px solid #638c72' },
        circle: { backgroundColor: '#638c72' },
    },
    [ACTIVE_ORDER_LABEL]: {
        tag: { backgroundColor: '#84ca8130', border: '1.5px solid #638c72' },
        circle: { backgroundColor: '#638c72' },
    },
    Queued: {
        tag: { backgroundColor: '#88d9ff38', border: '1.5px solid #0084ff' },
        circle: { backgroundColor: '#0084ff' },
    },
    Processing: {
        tag: { backgroundColor: '#f8d43236', border: '1.5px solid #ffa217' },
        circle: { backgroundColor: '#ffa217' },
    },
    Errored: {
        tag: {
            backgroundColor: 'rgba(255, 0, 0, 0.05)',
            border: '1.5px solid #d64759',
        },
        circle: { backgroundColor: '#d64759' },
    },
    Cancelled: {
        tag: { backgroundColor: '#79797936', border: '1.5px solid #929292' },
        circle: { backgroundColor: '#929292' },
    },
};

/**
 * Map an order/number-block status to its display pill, using the shared
 * OrderStatuses lookup (also used for the Orders table's status filter).
 * Accepts either the numeric code or an equivalent display-name string.
 *
 * @returns {{ label: 'Completed' | 'Queued' | 'Processing' | 'Errored' | 'Cancelled', key: string }}
 */
function getStatusDisplay(status) {
    const s = String(status ?? '')
        .trim()
        .toLowerCase();

    if (Object.prototype.hasOwnProperty.call(OrderStatuses, s)) {
        const { displayedName } = OrderStatuses[s];
        return { label: displayedName, key: displayedName };
    }

    const byLabel = Object.values(OrderStatuses).find(
        (entry) => entry.displayedName.toLowerCase() === s,
    );
    if (byLabel) {
        return { label: byLabel.displayedName, key: byLabel.displayedName };
    }

    // Any other in-flight/unrecognised state
    return { label: 'Processing', key: 'Processing' };
}

export default function OrderStatusPill({
    status,
    uploadOrderID,
    errorMessage,
}) {
    const { label, key } = isActiveOrder(status, uploadOrderID)
        ? { label: ACTIVE_ORDER_LABEL, key: ACTIVE_ORDER_LABEL }
        : getStatusDisplay(status);
    const pill = ORDER_STATUS_PILL_MAP[key] ?? ORDER_STATUS_PILL_MAP.Processing;
    const isError = key === 'Errored';

    const pillEl = (
        <div
            style={{
                borderRadius: '100px',
                paddingLeft: '4px',
                paddingRight: '12px',
                fontWeight: 500,
                height: '26px',
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '12px',
                cursor: isError && errorMessage ? 'help' : 'default',
                ...pill.tag,
            }}>
            <div
                style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    marginRight: '8px',
                    marginLeft: '6px',
                    flexShrink: 0,
                    ...pill.circle,
                }}
            />
            {label}
        </div>
    );

    if (isError && errorMessage) {
        return (
            <Tooltip
                label={
                    <Box>
                        <Box
                            fontWeight="bold"
                            fontSize="10px"
                            letterSpacing="wider"
                            mb={1}>
                            PROVISIONING ERROR
                        </Box>
                        <Box fontSize="xs">{errorMessage}</Box>
                    </Box>
                }
                hasArrow
                placement="top">
                {pillEl}
            </Tooltip>
        );
    }

    return pillEl;
}
